import { useIntl } from 'react-intl';
import { ReactNode, useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSentiment, TradingMessage } from 'pages/trading/model';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { useAuth0 } from '@auth0/auth0-react';
import { TbIcon } from 'components/Figma';
import { Typography } from '@mui/material';
import { useReadMessagesStore } from 'hooks/state/useReadMessagesStore';
import { cn } from 'utils/classNamesTailwind';
import { TradingMessageFeedback } from 'pages/trading/TradingMessageFeedback';
import { useMessageTagParser } from 'pages/trading/useMessageTagParser';
import { Tile } from 'components/Layout/PageContainer';

interface MessageDisplayProps {
  message: TradingMessage;
  allowNavigate?: boolean;
  className?: string;
  showFeedback?: boolean;
  showHeadline?: boolean;
  testId?: string;
}

interface MessageDisplayTileProps {
  message: TradingMessage;
  testId?: string;
}

const getIconForSentiment = (sentiment: MessageSentiment): ReactNode => {
  switch (sentiment) {
    case 'POSITIVE':
      return <TbIcon name="TrendUp" className="text-laurel-500" size="xl" />;
    case 'NEGATIVE':
      return <TbIcon name="TrendDown" className="text-coral-500" size="xl" />;
    default:
      return '';
  }
};

const getIconContainerClassnameForSentiment = (sentiment: MessageSentiment): string => {
  switch (sentiment) {
    case 'POSITIVE':
      return 'bg-laurel-50';
    case 'NEGATIVE':
      return 'bg-coral-50';
    default:
      return 'bg-snow-200';
  }
};

export function TradingMessageDisplay({
  message,
  allowNavigate = false,
  className = '',
  showHeadline = true,
  testId = undefined,
}: MessageDisplayProps) {
  const intl = useIntl();
  const { user } = useAuth0();
  const { accountNumber } = useContext(TenantConfigContext);
  const location = useLocation();
  const navigate = useNavigate();
  const parser = useMessageTagParser();

  const isMessageRead = useReadMessagesStore(state => state.isMessageRead);
  const markAsRead = useReadMessagesStore(state => state.markAsRead);
  const isRead = isMessageRead(accountNumber, user?.sub, message.messageId);

  const decorateMessage = (msg: TradingMessage): TradingMessage => {
    switch (msg.type) {
      case 'MARKETPLACE_ANALYSER': {
        const url = `${TRADING_ROOT}/detail/chnMarketplace/${msg.messageId}${location.search}`;
        return {
          ...msg,
          link: url,
          keyAttributes: ['chnMarketplace'],
          hiddenText: msg.hidden ? 'trading.message.hidden.four.percent' : undefined,
        };
      }
      case 'CATEGORY_ANALYSER': {
        const url = `${TRADING_ROOT}/detail/prdCategory/${msg.messageId}${location.search}`;
        return {
          ...msg,
          link: url,
          keyAttributes: ['prdCategory'],
          hiddenText: msg.hidden ? 'trading.message.hidden.four.percent' : undefined,
        };
      }
      case 'MARKET_SHARE_ANALYSER': {
        const url = `${TRADING_ROOT}/marketshare/${msg.messageId}${location.search}`;
        return { ...msg, link: url, keyAttributes: [], hiddenText: msg.hidden ? 'hidden' : undefined };
      }
      case 'PRICE_ANALYSER': {
        const url = `${TRADING_ROOT}/price-analysis/${msg.messageId}${location.search}`;
        return { ...msg, link: url, keyAttributes: ['chnName'] };
      }
      default:
        return { ...msg };
    }
  };

  const translatedMessage = (messageObject: TradingMessage) => {
    const msg =
      intl.locale.toString().toLowerCase().startsWith('de') && messageObject.message_de
        ? messageObject.message_de
        : messageObject.message_en;

    return parser.translateText(msg, messageObject);
  };

  const translatedHeadline = (messageObject: TradingMessage) => {
    const msg =
      intl.locale.toString().toLowerCase().startsWith('de') && messageObject.headline_de
        ? messageObject.headline_de
        : messageObject.headline_en;

    return parser.translateText(msg, messageObject);
  };

  const showDetails = (msg: TradingMessage) => {
    if (msg.link) {
      markAsRead(accountNumber, user?.sub, msg.messageId);
      navigate(msg.link);
    }
  };

  const decoratedMessage = decorateMessage(message);
  const baseTestId = testId ?? `trading-message-${message.messageId}`;

  const handleClick = allowNavigate ? () => showDetails(decoratedMessage) : undefined;
  const isClickable = handleClick !== undefined;
  const showFeedback = !isClickable;

  return (
    <Tile
      testId={`${baseTestId}-card`}
      className={cn('p-4 rounded-lg', className)}
      onClick={handleClick}
      onKeyDown={handleClick}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-4 flex items-center justify-center flex-shrink-0">
          <div
            className={cn(
              'w-[6px] h-[6px] rounded-full bg-ui-highlight transition-opacity',
              isRead ? 'opacity-0' : 'opacity-100'
            )}
            aria-hidden="true"
          />
        </div>
        <div
          className={cn(
            'w-8 h-8 flex items-center justify-center flex-grow-0 flex-shrink-0 rounded-lg',
            getIconContainerClassnameForSentiment(decoratedMessage.sentiment)
          )}
        >
          {getIconForSentiment(decoratedMessage.sentiment)}
        </div>
        <div className="flex flex-col items-start min-w-0 flex-1 ">
          {showHeadline && (decoratedMessage.headline_en || decoratedMessage.headline_de) && (
            <Typography
              fontSize="16px"
              sx={{ fontWeight: 600 }}
              className={cn(isClickable && 'underline group-hover/tile:text-denim-500')}
              data-testid={`${baseTestId}-headline`}
            >
              {translatedHeadline(decoratedMessage)}
            </Typography>
          )}
          <div className="whitespace-pre-wrap">
            <Typography fontSize="14px" sx={{ fontWeight: 300 }} data-testid={`${baseTestId}-body`}>
              {translatedMessage(decoratedMessage)}
            </Typography>
          </div>
        </div>
      </div>
      {showFeedback && (
        <div className="mt-3 pt-3 border-t border-snow-200">
          <TradingMessageFeedback message={decoratedMessage} />
        </div>
      )}
    </Tile>
  );
}

export function TradingMessageDisplayTile({ message, testId = undefined }: MessageDisplayTileProps) {
  return (
    <TradingMessageDisplay
      message={message}
      allowNavigate={false}
      showHeadline={true}
      testId={testId}
      className="bg-snow-50 hover:border-snow-50 border-snow-50 rounded-2xl col-span-6 xl:col-span-12"
    />
  );
}
